import { AppState } from '../store'
import { Organization, Scope, User } from '../types'
import { scopeHeadersSelector, scopeSelector } from './scope'
import { ScopeHeaders } from '../selectors/scope'
import { localeSelector } from './i18n'
import { isSessionValid } from '../util/session'
import { createSelector } from 'reselect'
import { IDFromV2 } from '../util'

export type AuthHeaders = ScopeHeaders & {
  'Content-Type': string
  'Accept-Language': string
  Authorization: string
  OrganizationID: string
}

export type AuthHeaderOverrides = {
  scope?: Scope
  asOwnerOverride?: string
}

export const authHeaderSelector = createSelector(
  [
    (state: AppState) => isSessionValid(state.session),
    (state: AppState) => state?.session?.authorizationToken ?? '',
    (state: AppState) => state?.session?.organization?.ID ?? '',
    localeSelector,
    scopeSelector,
    (state: AppState, overrides?: AuthHeaderOverrides) => overrides,
  ],
  (valid, token, orgID, locale, scope, overrides): AuthHeaders => {
    if (!valid) {
      return {} as AuthHeaders
    }

    if (overrides && overrides.scope) {
      scope = overrides.scope
    }

    let asOwner = IDFromV2(orgID)
    // AB#12017 - SM 6/30/2023
    // If an Owner override is present, use it explicitly.
    // See SAVE_LEAD in middleware/v3Leads for documentation on why this happens.
    if (overrides && overrides.asOwnerOverride && overrides.asOwnerOverride.length > 0) {
      asOwner = overrides.asOwnerOverride
    }

    const headers: ScopeHeaders = {
      'Owner-Scope': scope,
      'As-Owner': '',
    }

    switch (scope) {
      case Scope.IncludeDescendants:
      case Scope.Focused:
        headers['As-Owner'] = asOwner
    }

    return {
      'Content-Type': 'application/json',
      'Accept-Language': locale,
      Authorization: token,
      OrganizationID: orgID,
      ...headers,
    }
  }
)

export const authRequiredSelector = (state: AppState): boolean => !state?.session?.authorizationToken

export const sessionReadySelector = (state: AppState): boolean => {
  const hasToken = !!state?.session?.authorizationToken
  const hasSchemas = state?.schemas?.length > 0
  return hasToken && hasSchemas
}

export const focusedOrgSelector = (state: AppState): Organization | undefined => state?.session?.organization

export const userSelector = (state: AppState): User => state.session.user

export const loginErrorSelector = (state: AppState): string => state?.session?.error ?? ''

export const passwordResetErrorSelector = (state: AppState): string => state?.session?.error ?? ''

export const redirectSelector = (state: AppState): any => state?.session?.redirect

export const roleSelector = (state: AppState): any => state?.session?.context?.role ?? ''
